/**
 * The coach's saved plays (#92): loaded once, then kept in step with the
 * store as they save, open and delete — and with the board, which remembers
 * the saved play it came from.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { matchesPlay, playNameKey, type PlayDocument } from '../core/index.ts';
import type { PlayStore } from '../adapters/index.ts';
import type { BoardApi } from './useBoard.ts';

export interface SavedPlaysApi {
  /** Last saved first. */
  readonly saved: readonly PlayDocument[];
  /** Stored but unreadable here — saved by a newer version. Kept on the server, not shown. */
  readonly unreadable: number;
  readonly error: string | null;
  /** The saved play the board came from, if it is still there. */
  readonly current: PlayDocument | undefined;
  /** True when the board differs from `current` as it was last saved. */
  readonly unsaved: boolean;
  /** The saved play already using this name, which saving under it would replace. */
  named(name: string): PlayDocument | undefined;
  /** Updates `current` with the board. False when there is none (Save as… then asks for a name) or it failed. */
  save(): Promise<boolean>;
  /** Saves the board under `name` — into the play of that name if there is one — and links the board to it. */
  saveAs(name: string): Promise<boolean>;
  open(id: string): void;
  remove(id: string): Promise<void>;
  newPlay(): void;
}

const lastSavedFirst = (plays: readonly PlayDocument[]) => [...plays].sort((a, b) => b.updatedAt - a.updatedAt);

export function useSavedPlays(store: PlayStore, api: BoardApi, ready: boolean): SavedPlaysApi {
  const [saved, setSaved] = useState<readonly PlayDocument[]>([]);
  const [unreadable, setUnreadable] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    store
      .list()
      .then((list) => {
        if (cancelled) return;
        setSaved(lastSavedFirst(list.plays));
        setUnreadable(list.unreadable);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof Error ? cause.message : String(cause));
      });
    return () => {
      cancelled = true;
    };
  }, [store, ready]);

  const playId = api.doc.playId;
  const current = useMemo(() => (playId === undefined ? undefined : saved.find((p) => p.id === playId)), [saved, playId]);
  const unsaved = useMemo(() => current !== undefined && !matchesPlay(api.doc, current), [current, api.doc]);

  const named = useCallback(
    (name: string) => saved.find((p) => playNameKey(p.name) === playNameKey(name)),
    [saved],
  );

  const keep = useCallback(
    async (play: PlayDocument) => {
      try {
        await store.save(play);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : String(cause));
        return false;
      }
      setError(null);
      setSaved((list) => [play, ...list.filter((p) => p.id !== play.id)]);
      api.linkPlay(play);
      return true;
    },
    [store, api],
  );

  const save = useCallback(async () => {
    if (!current) return false;
    return keep(api.boardAsPlay(current.id, current.name, current.createdAt));
  }, [api, current, keep]);

  const saveAs = useCallback(
    async (name: string) => {
      if (!name.trim()) return false;
      const existing = named(name);
      return keep(api.boardAsPlay(existing?.id ?? api.newId('play'), name, existing?.createdAt));
    },
    [api, named, keep],
  );

  const open = useCallback(
    (id: string) => {
      const play = saved.find((p) => p.id === id);
      if (play) api.openSavedPlay(play);
    },
    [api, saved],
  );

  const remove = useCallback(
    async (id: string) => {
      try {
        await store.remove(id);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : String(cause));
        return;
      }
      setError(null);
      setSaved((list) => list.filter((p) => p.id !== id));
      // The board stays as it is; there is just no saved play behind it any more.
      if (id === playId) api.unlinkPlay();
    },
    [store, api, playId],
  );

  return { saved, unreadable, error, current, unsaved, named, save, saveAs, open, remove, newPlay: api.startNewPlay };
}
